import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, FormContainer, Modal, Text, Title } from '@wyz/ui';
import * as React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { TextField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import {
  CriteriaEditableBadge,
  CriteriaValueInput,
  CriteriaVisibilityRules,
} from '~/connect/components';
import { CriteriaHelpers } from '~/connect/helpers';
import { criteriaFormModalSchema } from '~/connect/schemas';
import {
  ICriteria,
  ICriteriaFormModal,
  ICriteriaPayload,
} from '~/connect/types';

interface CriteriaFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ICriteriaPayload) => void;
  automakerGroupOptions: Array<{ label: string; value: string }>;
  criteria?: ICriteria;
  isSubmitting?: boolean;
}

export const CriteriaFormModal: React.FC<CriteriaFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  automakerGroupOptions,
  criteria,
  isSubmitting = false,
}) => {
  const { t } = useI18n();
  const isEdition = criteria !== undefined;

  const methods = useForm<ICriteriaFormModal>({
    resolver: zodResolver(criteriaFormModalSchema),
    defaultValues: CriteriaHelpers.criteriaToForm(criteria),
  });

  const { fields, append, remove, update } = useFieldArray({
    control: methods.control,
    name: 'values',
    keyName: 'key',
  });

  React.useEffect(() => {
    if (open) {
      methods.reset(CriteriaHelpers.criteriaToForm(criteria));
    }
  }, [open, criteria, methods]);

  const handleRemoveValue = (index: number) => {
    const removedUid = methods.getValues(`values.${index}.uid`);
    remove(index);
    methods.setValue(
      'visibilityRules',
      methods.getValues('visibilityRules').map((rule) => ({
        ...rule,
        valueUids: rule.valueUids.filter((uid) => uid !== removedUid),
      })),
    );
  };

  const handleSubmit = (data: ICriteriaFormModal) => {
    onSubmit(CriteriaHelpers.formToPayload(data));
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal.Container open={open} onClose={handleClose} size='lg'>
      <Modal.Header variant='light'>
        <Title heading='page-title' weight='bold'>
          <Trans
            i18nKey={
              isEdition
                ? 'app.criteria.configure.modal.title'
                : 'app.criteria.create.modal.title'
            }
          />
        </Title>
      </Modal.Header>
      <FormContainer methods={methods} submitHandler={handleSubmit}>
        <Modal.Body>
          <div className='d-flex flex-column gap-5'>
            <div className='d-flex flex-column gap-3'>
              <Text heading='normal' weight='semi-bold'>
                <Trans i18nKey='app.criteria.form.modal.general_information' />
              </Text>
              <TextField
                name='name'
                label={t('app.criteria.create.modal.criteria_name')}
                placeholder={t(
                  'app.criteria.create.modal.criteria_name.placeholder',
                )}
                required
              />
              <div className='d-flex flex-column gap-2'>
                <Text color='secondary' heading='sm'>
                  <Trans i18nKey='app.criteria.create.modal.values' />
                </Text>
                <Flex wrap='wrap' className='gap-2 align-items-center'>
                  {fields.map((field, index) => (
                    <CriteriaEditableBadge.Root
                      key={field.key}
                      value={field.label}
                      onSubmit={(newValue) =>
                        update(index, {
                          ...methods.getValues(`values.${index}`),
                          label: newValue,
                        })
                      }
                    >
                      <CriteriaEditableBadge.Display
                        value={field.label}
                        reserved={false}
                        isDefault={Boolean(field.default)}
                        onDelete={() => handleRemoveValue(index)}
                      />
                      <CriteriaEditableBadge.Input
                        name={`values.${index}.label`}
                      />
                    </CriteriaEditableBadge.Root>
                  ))}
                  <CriteriaValueInput.Root
                    onSubmit={(newValue) => {
                      append({
                        uid: CriteriaHelpers.createValueUid(),
                        label: newValue,
                      });
                    }}
                  >
                    <CriteriaValueInput.Trigger id={`add-new-value`} />
                    <CriteriaValueInput.Input name='newValue' />
                  </CriteriaValueInput.Root>
                </Flex>
                {methods.formState.errors.values && (
                  <Text color='danger' heading='xs'>
                    <Trans i18nKey='app.criteria.create.modal.values.required' />
                  </Text>
                )}
              </div>
            </div>
            <CriteriaVisibilityRules
              automakerGroupOptions={automakerGroupOptions}
            />
          </div>
        </Modal.Body>
        <Modal.Footer variant='light'>
          <Flex className='w-100 gap-3'>
            <Button
              id='cancel-create-criteria'
              type='button'
              variant='secondary'
              className='flex-grow-1'
              onClick={handleClose}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            <Button
              id={
                isEdition
                  ? 'submit-configure-criteria'
                  : 'submit-create-criteria'
              }
              type='submit'
              variant='primary'
              className='flex-grow-1'
              disabled={isSubmitting}
            >
              <Trans
                i18nKey={
                  isEdition
                    ? 'app.criteria.configure.modal.submit'
                    : 'app.common.buttons.validate'
                }
              />
            </Button>
          </Flex>
        </Modal.Footer>
      </FormContainer>
    </Modal.Container>
  );
};
